import { useState } from 'react';
import { Link } from 'react-router-dom';

import { ApiError } from '../../../api/errors';
import { useAuth } from '../../../auth/AuthProvider';
import { permissions } from '../../../auth/permissions';
import { AddFacilityDialog } from '../components/AddFacilityDialog';
import { useCreateFacility, useFacilities } from '../api/useFacilities';

function errorText(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return 'Route42 could not complete the request.';
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
}

export function OrganizationPage() {
  const auth = useAuth();
  const facilitiesQuery = useFacilities();
  const createMutation = useCreateFacility();
  const [showCreate, setShowCreate] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  if (!auth.can(permissions.organizationRead) || !auth.can(permissions.facilitiesRead)) {
    return <section className="r42-page r42-page--wide"><div className="r42-empty-state"><i className="bi bi-shield-lock" aria-hidden="true" /><h2>Organization settings are not available</h2><p>Your current Route42 membership does not include permission to view organization settings.</p></div></section>;
  }

  const customer = facilitiesQuery.data?.customer;
  const facilities = facilitiesQuery.data?.facilities ?? [];
  const entitlements = customer?.entitlements;
  const maxMachines = entitlements?.limits?.max_machines ?? null;
  const maxUsers = entitlements?.limits?.max_users ?? null;
  const canCreate = auth.can(permissions.facilitiesCreate) && !auth.activeMembership?.facility_ids.length;

  return (
    <section className="r42-page r42-page--wide r42-organization-page">
      <div className="r42-page-header r42-organization-header">
        <div>
          <span className="r42-eyebrow">Organization settings</span>
          <h1>Organization</h1>
          <p>Review your Route42 organization and manage the facilities used to organize machines and access.</p>
        </div>
        {canCreate ? <button className="btn r42-btn-primary" onClick={() => { setNotice(null); setShowCreate(true); }}><i className="bi bi-building-add" aria-hidden="true" /> Add facility</button> : null}
      </div>

      {notice ? <div className="r42-user-notice" role="status"><i className="bi bi-check-circle" aria-hidden="true" /><span>{notice}</span><button onClick={() => setNotice(null)} aria-label="Dismiss message"><i className="bi bi-x-lg" aria-hidden="true" /></button></div> : null}

      <div className="r42-organization-summary" aria-label="Organization information">
        <div><span className="r42-eyebrow">Organization</span><strong>{customer?.customer_name ?? auth.activeMembership?.customer_name ?? 'Route42 organization'}</strong><small>{customer?.customer_id ?? auth.activeCustomerId}</small></div>
        <div><span className="r42-eyebrow">Status</span><strong>{customer?.status ?? auth.activeMembership?.customer_status ?? '—'}</strong><small>Tenant access state</small></div>
        <div><span className="r42-eyebrow">Plan</span><strong>{entitlements?.display_name ?? entitlements?.plan_id ?? 'MVP'}</strong><small>{maxMachines ? `${maxMachines} machines` : 'Machine limit not specified'}{maxUsers ? ` · ${maxUsers} users` : ''}</small></div>
        <div><span className="r42-eyebrow">Facilities</span><strong>{facilities.length}</strong><small>{auth.activeMembership?.facility_ids.length ? 'Your assigned scope' : 'Organization directory'}</small></div>
      </div>

      <div className="r42-organization-section-head">
        <div><span className="r42-eyebrow">Facility directory</span><h2>Facilities</h2><p>Facility names are used throughout Route42 instead of exposing internal facility IDs.</p></div>
        {auth.can(permissions.usersRead) ? <Link className="btn btn-outline-secondary btn-sm" to="/settings/users">Users & access</Link> : null}
      </div>

      {facilitiesQuery.isPending ? (
        <div className="r42-list-panel" aria-label="Loading facilities">{[1, 2].map((item) => <div className="r42-skeleton-row" key={item}><span /><div><b /><small /></div></div>)}</div>
      ) : facilitiesQuery.isError ? (
        <div className="r42-inline-state r42-inline-state--error"><span>{errorText(facilitiesQuery.error)}</span><button className="btn btn-outline-secondary btn-sm" onClick={() => void facilitiesQuery.refetch()}>Try again</button></div>
      ) : facilities.length === 0 ? (
        <div className="r42-empty-state r42-facility-empty"><i className="bi bi-buildings" aria-hidden="true" /><h2>No facilities yet</h2><p>Machines can remain organization-wide, or an administrator can add the first facility.</p>{canCreate ? <button className="btn r42-btn-primary" onClick={() => setShowCreate(true)}>Add facility</button> : null}</div>
      ) : (
        <div className="r42-facility-list" aria-label="Organization facilities">
          <div className="r42-facility-list-head" aria-hidden="true"><span>Facility</span><span>Location</span><span>Status</span><span>Added</span></div>
          {facilities.map((facility) => <div className="r42-facility-row" key={facility.facility_id}>
            <div><span className="r42-facility-icon"><i className="bi bi-building" aria-hidden="true" /></span><div><strong>{facility.facility_name}</strong><small>Route42 facility</small></div></div>
            <span>{facility.location ?? 'Location not specified'}</span>
            <span className={`r42-user-status r42-user-status--${facility.status.toLocaleLowerCase()}`}>{facility.status === 'ACTIVE' ? 'Active' : facility.status}</span>
            <span>{formatDate(facility.created_at)}</span>
          </div>)}
        </div>
      )}

      {showCreate ? <AddFacilityDialog
        busy={createMutation.isPending}
        error={createMutation.isError ? errorText(createMutation.error) : null}
        onClose={() => { if (!createMutation.isPending) setShowCreate(false); }}
        onSubmit={async (input) => {
          try {
            const result = await createMutation.mutateAsync(input);
            setShowCreate(false);
            setNotice(`${result.facility.facility_name} was added to the organization.`);
          } catch { /* mutation state renders error */ }
        }}
      /> : null}
    </section>
  );
}
